import Honeybadger from '@honeybadger-io/js';
import { z } from 'zod';

// The error shape every V3 endpoint answers with. `API::V3::APIController`
// builds `status` from `Rack::Utils.status_code` and `error` from
// `HTTP_STATUS_CODES.fetch`, so both are always present on an error it handled.
//
// `message` is venuu's own addition and Rails' built-in JSON errors do not
// carry it, so an error the controller never handled arrives with `status` and
// `error` alone. Callers fall back to their own copy when it is missing, which
// makes its absence a valid body rather than a violated contract.
export const apiRequestFailureBody = z.object({
  status: z.number(),
  error: z.string(),
  message: z.string().nullish()
});

export type ApiRequestFailureBody = z.infer<typeof apiRequestFailureBody>;

// Callers are already handling a failed request, so this cannot throw: a body
// that does not match degrades to `null` and leaves them on their own copy.
// The report is what keeps a genuinely unexpected shape visible instead of
// silently becoming generic error copy.
export function parseApiRequestFailureBody(
  json: unknown
): ApiRequestFailureBody | null {
  // An absent body is the ordinary shape of an HTML error page or a bare 502,
  // not something worth reporting.
  if (json === null || json === undefined) {
    return null;
  }

  const result = apiRequestFailureBody.safeParse(json);
  if (!result.success) {
    Honeybadger.notify(new Error('Unexpected API error body'), {
      context: { json, issues: result.error.issues }
    });
    return null;
  }

  return result.data;
}

// The user-facing message out of an error body, or the caller's own copy when
// the server sent none. Callers hold their own fallback because the copy is
// domain-specific -- "could not save billing details" beats a generic string.
//
// An empty `message` counts as absent, matching how `apiRequestFailureToMessage`
// treats the same field: a blank string is not copy anyone can act on.
export function errorMessageFromBody(json: unknown, fallback: string): string {
  return parseApiRequestFailureBody(json)?.message || fallback;
}
